// CategoryBentoGrid, Pinterest-style masonry for the 7 service pillars.
//
// CSS columns do the masonry. Each tile cross-fades between 3 photos
// (admin-approved provider work first, stock filling the gaps), shows a
// live artist count and tappable specialty chips. On mobile the big
// featured tile cycles through every category. Rotation pauses on
// hover/touch, while off-screen, and for reduced-motion users.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { api } from '../../App';
import { usePrefersReducedMotion, useRotation } from '../../lib/useRotation';
import { TILES, TILE_CHIPS } from './indexTiles';

const PHOTO_MS = 6500;
const FEATURE_MS = 5000;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Narrow 2-column tiles need taller shapes so the caption has room.
const COMPACT_ASPECT = { 'aspect-[4/3]': 'aspect-square', 'aspect-square': 'aspect-[4/5]' };

// Same key Discover's "near you" search uses, so the postcode is shared both ways.
const POSTCODE_KEY = 'zuriya_near_you_postcode';
const readPostcode = () => { try { return window.localStorage.getItem(POSTCODE_KEY) || ''; } catch { return ''; } };

function useIndexData(postcode) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    api.get('/landing/index-tiles', { params: postcode ? { postcode } : {} })
      .then((res) => { if (live) setData(res.data); })
      .catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [postcode]);
  const tiles = useMemo(() => TILES.map((tile) => {
    const live = data?.pillars?.[tile.id];
    const real = (live?.photos || []).map((p) => ({
      image: p.url.startsWith('http') ? p.url : `${BACKEND}${p.url}`,
      alt: p.alt,
      provider: { name: p.provider_name, to: p.provider_username ? `/@${p.provider_username}` : `/provider/${p.provider_id}` },
    }));
    const count = live?.artist_count;
    const near = data?.scope === 'near_you';
    const countLabel = data ? (count ? `${count} artists ${near ? 'near you' : 'across the UK'}` : 'New artists joining') : null;
    const countShort = data ? (count ? `${count} artists ${near ? 'nearby' : 'in UK'}` : 'New artists joining') : null;
    return { ...tile, real, stock: tile.photos, countLabel, countShort };
  }), [data]);
  return { tiles, scope: data?.scope };
}

function TilePhotos({ photos, index, label, onBroken }) {
  // Only mount photos that have been shown or are up next, so idle tiles don't download all three.
  const [seen, setSeen] = useState(1);
  useEffect(() => { setSeen((n) => Math.max(n, Math.min(index + 2, photos.length))); }, [index, photos.length]);
  return photos.slice(0, seen).map((photo, i) => (
    <img
      key={photo.image}
      src={photo.image}
      alt={i === index ? (photo.alt || label) : ''}
      aria-hidden={i === index ? undefined : true}
      loading="lazy"
      onError={() => onBroken(photo.image)}
      className={`absolute inset-0 w-full h-full object-cover transition-[opacity,transform] duration-[1200ms] ease-out group-hover:scale-[1.05] ${i === index ? 'opacity-100' : 'opacity-0'}`}
    />
  ));
}

function PhotoCredit({ photo, tileId }) {
  if (!photo.credit) return null;
  // Not an <a>: the whole tile is already a link.
  const open = (e) => { e.preventDefault(); e.stopPropagation(); window.open(photo.credit.url, '_blank', 'noopener,noreferrer'); };
  return (
    <span role="link" tabIndex={0} onClick={open} onKeyDown={(e) => { if (e.key === 'Enter') open(e); }}
      title={`Photographer: ${photo.credit.name} on Pexels`} data-testid={`bento-credit-${tileId}`}
      className="absolute top-3 left-3 cursor-pointer text-[9px] tracking-[0.18em] uppercase font-mono px-2 py-1 rounded-sm text-white/90 bg-black/40 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-black/60 hover:text-white">
      {photo.credit.name} <span className="text-white/60">&middot; Pexels</span>
    </span>
  );
}

function SpecialtyChips({ tile, compact, testPrefix }) {
  const chips = (TILE_CHIPS[tile.id] || []).slice(0, compact ? 2 : 3);
  return (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${testPrefix}-chips-${tile.id}`}>
      {chips.map(([id, label]) => (
        <Link key={id} to={`/discover?category=${tile.id}&specialty=${id}`}
          className="text-[11px] leading-none px-2.5 py-1.5 rounded-full border border-stone-300 bg-white/70 text-stone-700 hover:border-[#1A1A1A] hover:text-[#1A1A1A] transition-colors"
          data-testid={`${testPrefix}-chip-${tile.id}-${id}`}>
          {label}
        </Link>
      ))}
    </div>
  );
}

function TileCaption({ tile, photo, compact }) {
  return (
    <div className={`absolute inset-x-0 bottom-0 text-white ${compact ? 'p-3' : 'p-4 lg:p-5'}`}>
      {tile.countLabel && (
        <p className={`font-mono uppercase text-white/85 mb-1.5 whitespace-nowrap truncate ${compact ? 'text-[9px] tracking-[0.12em]' : 'text-[10px] tracking-[0.18em]'}`}
          data-testid={`bento-count-${tile.id}`}>
          {compact ? tile.countShort : tile.countLabel}
        </p>
      )}
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0 flex gap-2.5">
          <span aria-hidden="true" className="block w-[3px] shrink-0 self-stretch rounded-full" style={{ backgroundColor: tile.accent }} />
          <div className="min-w-0">
            <h3 className={`font-heading leading-tight tracking-tight break-words ${compact ? 'text-base' : 'text-xl lg:text-2xl'}`}>
              {tile.label}
            </h3>
            {!compact && (
              <p className="text-xs lg:text-sm text-white/80 mt-1 max-w-md leading-relaxed line-clamp-2">{tile.blurb}</p>
            )}
            {photo.provider && (
              <p className="text-[10px] text-white/75 mt-1 truncate" data-testid={`bento-provider-credit-${tile.id}`}>
                Work by {photo.provider.name}
              </p>
            )}
          </div>
        </div>
        {!compact && (
          <span aria-hidden="true"
            className="shrink-0 w-9 h-9 rounded-full border border-white/40 flex items-center justify-center transition-[transform,background-color,border-color] duration-300 group-hover:rotate-[20deg]"
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = tile.accent; e.currentTarget.style.borderColor = tile.accent; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; e.currentTarget.style.borderColor = ''; }}>
            <ArrowUpRight className="h-4 w-4" />
          </span>
        )}
      </div>
      <span aria-hidden="true" className="block h-px mt-3 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out" style={{ backgroundColor: tile.accent }} />
    </div>
  );
}

function Tile({ tile, photoIndex = 0, compact = false, testId }) {
  // Provider photos come first; if one fails to load, stock steps in.
  const [broken, setBroken] = useState(() => new Set());
  const real = tile.real.filter((p) => !broken.has(p.image));
  const photos = [...real, ...tile.stock].slice(0, Math.max(3, real.length));
  const markBroken = (image) => setBroken((prev) => (prev.has(image) ? prev : new Set(prev).add(image)));
  const index = photoIndex % photos.length;
  const photo = photos[index];
  const aspect = compact ? (COMPACT_ASPECT[tile.aspect] || tile.aspect) : tile.aspect;
  const id = testId || (compact ? `bento-mobile-${tile.id}` : `bento-category-${tile.id}`);
  return (
    <div className="break-inside-avoid mb-4 lg:mb-5">
      <Link to={`/discover?category=${tile.id}`} className="block group relative overflow-hidden bg-stone-900"
        data-testid={id} title={photo.alt || tile.label}>
        <div className={`relative w-full ${aspect}`}>
          <TilePhotos photos={photos} index={index} label={tile.label} onBroken={markBroken} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/5" />
          <span className={`absolute top-3 right-3 uppercase font-mono border text-white/95 bg-black/55 backdrop-blur-sm ${compact ? 'text-[9px] tracking-[0.18em] px-2 py-0.5' : 'text-[10px] tracking-[0.25em] px-2.5 py-1'}`}
            style={{ borderColor: tile.accent }}>
            {tile.kicker}
          </span>
          <PhotoCredit photo={photo} tileId={tile.id} />
          <TileCaption tile={tile} photo={photo} compact={compact} />
        </div>
      </Link>
      <SpecialtyChips tile={tile} compact={compact} testPrefix={id} />
    </div>
  );
}

function RotatingTile({ tile, order, active, compact }) {
  const photoIndex = useRotation(Math.max(3, tile.real.length), { ms: PHOTO_MS, offset: 2500 + order * 1100, active });
  return <Tile tile={tile} photoIndex={photoIndex} compact={compact} />;
}

function FeaturedTile({ tiles, active }) {
  // One tick walks through every category, then moves each on to its next photo.
  const tick = useRotation(tiles.length * 3, { ms: FEATURE_MS, active });
  const tile = tiles[tick % tiles.length];
  const round = Math.floor(tick / tiles.length);
  return (
    <div key={tile.id} className="animate-in fade-in duration-700" data-testid="bento-featured" data-featured-category={tile.id}>
      <Tile tile={{ ...tile, aspect: 'aspect-[4/5]' }} photoIndex={round} testId="bento-featured-tile" />
    </div>
  );
}

const UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/;

function PostcodeBox({ postcode, setPostcode, scope }) {
  const [draft, setDraft] = useState(postcode);
  const [error, setError] = useState('');
  const save = (value) => {
    try {
      if (value) window.localStorage.setItem(POSTCODE_KEY, value);
      else window.localStorage.removeItem(POSTCODE_KEY);
    } catch { /* private mode */ }
    setPostcode(value);
  };
  const submit = (e) => {
    e.preventDefault();
    const cleaned = draft.trim().toUpperCase().replace(/\s+/g, ' ');
    if (!UK_POSTCODE.test(cleaned)) { setError('Enter a full UK postcode, e.g. S1 2HE'); return; }
    setError('');
    setDraft(cleaned);
    save(cleaned);
  };
  const clear = () => { setDraft(''); setError(''); save(''); };
  const local = postcode && scope === 'near_you';
  return (
    <form onSubmit={submit} className="mt-5" data-testid="index-postcode-form">
      <label htmlFor="index-postcode" className="block text-xs font-mono uppercase tracking-[0.18em] text-stone-500 mb-2">
        {local ? `Showing artists within 20 miles of ${postcode}` : 'Showing artists across the UK'}
      </label>
      <div className="flex gap-2">
        <input id="index-postcode" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Your postcode"
          autoComplete="postal-code" maxLength={8}
          className="flex-1 min-w-0 h-10 px-3 rounded-full border border-stone-300 bg-white text-sm uppercase placeholder:normal-case focus:outline-none focus:border-[#1A1A1A]"
          data-testid="index-postcode-input" />
        <button type="submit" className="h-10 px-4 rounded-full bg-[#1A1A1A] text-white text-sm hover:bg-black transition-colors" data-testid="index-postcode-submit">
          {local ? 'Update' : 'Near me'}
        </button>
        {postcode && (
          <button type="button" onClick={clear} className="h-10 px-3 text-sm text-stone-500 hover:text-[#1A1A1A]" data-testid="index-postcode-clear">
            UK-wide
          </button>
        )}
      </div>
      {error && <p className="text-xs text-red-600 mt-2" data-testid="index-postcode-error">{error}</p>}
      {postcode && scope === 'uk' && (
        <p className="text-xs text-stone-500 mt-2" data-testid="index-postcode-unknown">We could not find that postcode, so counts are UK-wide.</p>
      )}
    </form>
  );
}

// Rotation runs only while the section is on screen and not being touched or hovered.
function useRotationActive(ref) {
  const reduced = usePrefersReducedMotion();
  const [visible, setVisible] = useState(false);
  const [held, setHeld] = useState(false);
  const releaseTimer = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  useEffect(() => () => clearTimeout(releaseTimer.current), []);

  const hold = () => { clearTimeout(releaseTimer.current); setHeld(true); };
  const release = (delay) => {
    clearTimeout(releaseTimer.current);
    releaseTimer.current = setTimeout(() => setHeld(false), delay);
  };
  const handlers = {
    onMouseEnter: hold, onMouseLeave: () => release(0),
    onTouchStart: hold, onTouchEnd: () => release(4000), onTouchCancel: () => release(4000),
  };
  return { active: visible && !held && !reduced, handlers };
}

export function CategoryBentoGrid() {
  const [postcode, setPostcode] = useState(readPostcode);
  const { tiles, scope } = useIndexData(postcode);
  const sectionRef = useRef(null);
  const { active, handlers } = useRotationActive(sectionRef);
  return (
    <section
      ref={sectionRef}
      {...handlers}
      className="bg-[#F9F6F0] paper-grain border-t border-stone-200 py-20 lg:py-28"
      data-testid="categories-bento"
      data-rotating={active ? 'true' : 'false'}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12 lg:mb-16">
          <div className="max-w-2xl">
            <span className="overline">The Index</span>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-[#1A1A1A] mt-3 leading-[1.05]">
              Seven crafts. <span className="italic text-stone-500">One directory.</span>
            </h2>
          </div>
          <div className="max-w-sm">
            <p className="text-stone-500 text-base leading-relaxed">
              Seven disciplines, each with its own bench of UK artists, every heritage and texture welcome. Pin a pillar to open the spread.
            </p>
            <PostcodeBox postcode={postcode} setPostcode={setPostcode} scope={scope} />
          </div>
        </div>

        {/* Mobile: a featured tile that cycles through every pillar, then the
            rest in a compact 2-column masonry. Desktop: one multi-column masonry. */}
        <div className="lg:hidden mb-2">
          <FeaturedTile tiles={tiles} active={active} />
        </div>
        <div className="lg:hidden columns-2 gap-3" data-testid="bento-masonry-mobile">
          {tiles.slice(1).map((tile, i) => (
            <RotatingTile key={tile.id} tile={tile} order={i} active={active} compact />
          ))}
        </div>

        <div className="hidden lg:block columns-3 xl:columns-4 gap-4" data-testid="bento-masonry">
          {tiles.map((tile, i) => (
            <RotatingTile key={tile.id} tile={tile} order={i} active={active} />
          ))}
        </div>
      </div>
    </section>
  );
}
